import { useMemo } from 'react';
import { format, isToday } from 'date-fns';
import { ChevronLeft, ChevronRight, Bell, BellOff, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';

const SITES = {
  specialist_dental_london: { label: 'SDL', dot: '#C8A86B' },
  shihab_romeed:            { label: 'SR',  dot: '#44403C' },
  london_dental_academy:    { label: 'LDA', dot: '#3B82F6' },
};

const VIEW_OPTIONS = [
  { key: 'day', label: 'Day', shortcut: 'D' },
  { key: 'week', label: 'Week', shortcut: 'W' },
  { key: 'month', label: 'Month', shortcut: 'M' },
];

export default function CalendarToolbar({
  date,
  view,
  onNavigate,
  onView,
  // Custom props
  activeSites = [],
  onToggleSite,
  providers = [],
  selectedProviders = [],
  onToggleProvider,
  todayCount = 0,
  nextAppointment = null,
  pushEnabled = false,
  onTogglePush,
  siteAccess = [],
}) {
  const dateLabel = useMemo(() => {
    switch (view) {
      case 'day':
        return format(date, 'EEEE, d MMMM yyyy');
      case 'week':
        return format(date, 'MMMM yyyy');
      case 'month':
        return format(date, 'MMMM yyyy');
      default:
        return format(date, 'MMMM yyyy');
    }
  }, [date, view]);

  return (
    <div className="flex flex-col gap-3 pb-4">
      {/* Row 1: Page header + Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">Operations</p>
          <h2 className="text-2xl lg:text-3xl font-playfair font-medium text-stone-900 tracking-tight mt-1">Calendar</h2>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Site filter dots */}
          <div className="flex gap-1.5">
            {Object.entries(SITES)
              .filter(([key]) => siteAccess.includes(key))
              .map(([key, cfg]) => {
                const active = activeSites.includes(key);
                return (
                  <button
                    key={key}
                    onClick={() => onToggleSite?.(key)}
                    className={cn(
                      'flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-lg border transition-all',
                      active
                        ? 'bg-white border-stone-300 text-stone-900 shadow-sm'
                        : 'bg-stone-50 border-stone-100 text-stone-400'
                    )}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0 transition-opacity"
                      style={{ backgroundColor: cfg.dot, opacity: active ? 1 : 0.3 }}
                    />
                    {cfg.label}
                  </button>
                );
              })}
          </div>

          {/* Provider filter */}
          {providers.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <button className={cn(
                  'flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-lg border transition-all',
                  selectedProviders.length > 0
                    ? 'bg-white border-stone-300 text-stone-900 shadow-sm'
                    : 'bg-stone-50 border-stone-100 text-stone-500 hover:border-stone-200'
                )}>
                  <Users size={14} />
                  {selectedProviders.length > 0
                    ? `${selectedProviders.length} provider${selectedProviders.length !== 1 ? 's' : ''}`
                    : 'Providers'}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-3" align="end">
                <p className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-2">Filter by Provider</p>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {providers.map(p => (
                    <label
                      key={p.id}
                      className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-stone-50 cursor-pointer transition-colors"
                    >
                      <Checkbox
                        checked={selectedProviders.includes(p.id)}
                        onCheckedChange={() => onToggleProvider?.(p.id)}
                        className="h-4 w-4"
                      />
                      <span className="text-sm text-stone-700">{p.name}</span>
                    </label>
                  ))}
                </div>
                {selectedProviders.length > 0 && (
                  <button
                    onClick={() => selectedProviders.forEach(id => onToggleProvider?.(id))}
                    className="text-xs text-stone-500 hover:text-stone-800 mt-2 pt-2 border-t border-stone-100 w-full text-left"
                  >
                    Clear all
                  </button>
                )}
              </PopoverContent>
            </Popover>
          )}

          {/* Push notification toggle */}
          <button
            onClick={onTogglePush}
            className={cn(
              'flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border transition-all',
              pushEnabled
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : 'bg-stone-50 border-stone-100 text-stone-400 hover:border-stone-200 hover:text-stone-600'
            )}
            title={pushEnabled ? 'Notifications enabled' : 'Enable push notifications'}
          >
            {pushEnabled ? <Bell size={14} /> : <BellOff size={14} />}
            <span className="hidden sm:inline">{pushEnabled ? 'Notifications on' : 'Notify me'}</span>
          </button>
        </div>
      </div>

      {/* Row 2: Calendar Nav + View Switcher */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white rounded-xl border border-stone-200 shadow-sm px-4 py-2.5">
        {/* Left: Navigation */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => onNavigate('PREV')}
            className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-600 transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => onNavigate('TODAY')}
            className={cn(
              'flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg border transition-colors',
              isToday(date)
                ? 'bg-stone-900 text-white border-stone-900'
                : 'border-stone-200 text-stone-600 hover:bg-stone-100'
            )}
          >
            Today
            {todayCount > 0 && (
              <span className={cn(
                'inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold px-1',
                isToday(date) ? 'bg-white/20 text-white' : 'bg-stone-200 text-stone-600'
              )}>
                {todayCount}
              </span>
            )}
          </button>
          <button
            onClick={() => onNavigate('NEXT')}
            className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-600 transition-colors"
          >
            <ChevronRight size={18} />
          </button>
          <h3 className="text-base sm:text-lg font-playfair font-medium text-stone-900 tracking-tight ml-2">
            {dateLabel}
          </h3>
        </div>

        {/* Right: View Switcher */}
        <div className="flex items-center bg-stone-100 p-1 rounded-xl border border-stone-200/60">
          {VIEW_OPTIONS.map(opt => (
            <button
              key={opt.key}
              onClick={() => onView(opt.key)}
              className={cn(
                'flex items-center gap-1 px-3 sm:px-4 py-1.5 rounded-lg text-xs font-semibold transition-all',
                view === opt.key
                  ? 'bg-white text-stone-900 shadow-sm'
                  : 'text-stone-500 hover:text-stone-800'
              )}
            >
              {opt.label}
              <kbd className={cn(
                'hidden lg:inline-block text-[9px] px-1 py-0.5 rounded font-mono',
                view === opt.key ? 'bg-stone-100 text-stone-500' : 'bg-stone-200/50 text-stone-400'
              )}>
                {opt.shortcut}
              </kbd>
            </button>
          ))}
        </div>
      </div>

      {/* Today summary bar */}
      {todayCount > 0 && (
        <div className="flex items-center gap-3 bg-amber-50/60 border border-amber-200/50 rounded-xl px-4 py-2.5 text-sm">
          <span className="text-amber-600">📅</span>
          <span className="text-stone-700">
            <span className="font-semibold">Today</span> — {todayCount} appointment{todayCount !== 1 ? 's' : ''}
            {nextAppointment && (
              <span className="text-stone-500">
                {' · Next: '}
                <span className="font-medium text-stone-700">
                  {nextAppointment.data?.serviceName || nextAppointment.title}
                </span>
                {' at '}
                <span className="font-medium text-stone-700">
                  {format(nextAppointment.start, 'HH:mm')}
                </span>
              </span>
            )}
          </span>
        </div>
      )}
    </div>
  );
}
